import {
    ContentType,
    GroupType,
    IGroupPictureFileMetadataResponse,
    IPictureFileMetadataResponse,
} from '@kcftech/prometheustypescriptapiclient';
import { sdClient } from 'contexts/SDKClientContext';
import {
    BaseDataSourceOptions,
    DataSourceQuery,
} from 'custom_hooks/data_hooks/data_source/data_source';
import {
    InfiniteDataSource,
    useInfiniteDataSource,
} from 'custom_hooks/data_hooks/data_source/useInfiniteDataSource';
import {
    PagedDataSource,
    usePagedDataSource,
} from 'custom_hooks/data_hooks/data_source/usePagedDataSource';
import { queryClient } from 'utils/query';
import {
    buildOrderBy,
    constructSearchExpression,
    Filter,
    toDataSourceResult,
} from 'utils/query_v3';
import { sentryCaptureMessage } from 'utils/sentry';

export type FileLibraryFilter = Readonly<{
    groupIds?: string[] | undefined;
    locationId?: string | undefined;
    corporationId?: string | undefined;
    nameSearch: string | undefined;
    fileTypes?: ContentType[] | undefined;
    excludeFolders?: boolean | undefined;
    parentFolderId?: string | undefined;
    isRootFolder?: boolean;
}>;

export type UseFileLibraryDataSourceOptions = BaseDataSourceOptions<
    IPictureFileMetadataResponse,
    FileLibraryFilter
>;

const getAllFiles: DataSourceQuery<IPictureFileMetadataResponse, FileLibraryFilter> = ({
    filter: {
        groupIds,
        nameSearch,
        locationId,
        corporationId,
        fileTypes,
        excludeFolders,
        parentFolderId,
        isRootFolder,
    },
    sort,
    page,
    pageLimit,
}) => {
    const filter = Filter.and(
        locationId ? Filter.eq('LocationId', locationId) : undefined,
        // We cannot supply BOTH a corporationId AND locationId to the API
        corporationId && !locationId
            ? Filter.eq('CorporationId', corporationId)
            : undefined,
        fileTypes ? Filter.in('FileType', fileTypes) : undefined,
        excludeFolders ? Filter.neq('FileType', ContentType.Folder) : undefined,
        parentFolderId ? Filter.eq('ParentFolderId', parentFolderId) : undefined,
        isRootFolder ? Filter.eq('isRootFolder', true) : undefined
    );

    const orderBy = buildOrderBy(sort);

    return toDataSourceResult(
        sdClient.getFiles({ groupIds, filter, page, pageLimit, orderBy, nameSearch })
    );
};

export type FileLibraryPagedDataSource = PagedDataSource<
    IPictureFileMetadataResponse,
    FileLibraryFilter
>;

export function usePagedFileLibraryDataSource({
    ...options
}: UseFileLibraryDataSourceOptions): FileLibraryPagedDataSource {
    return usePagedDataSource({
        ...options,
        query: getAllFiles,
    });
}

export type FileLibraryInfiniteDataSource = InfiniteDataSource<
    IPictureFileMetadataResponse,
    FileLibraryFilter
>;

export function useInfiniteFileLibraryDataSource({
    ...options
}: UseFileLibraryDataSourceOptions): FileLibraryInfiniteDataSource {
    return useInfiniteDataSource({
        ...options,
        query: getAllFiles,
    });
}

export const AllFilesGroupTypes: FilesGroupType[] = [
    GroupType.Assembly,
    GroupType.Asset,
    GroupType.MonitoringPoint,
];

function isAllOrNoFilter(groupType: FilesGroupType[] | undefined): boolean {
    return (
        !groupType ||
        groupType.length === 0 ||
        groupType.length === AllFilesGroupTypes.length
    );
}

export type FilesGroupType =
    | GroupType.Assembly
    | GroupType.Asset
    | GroupType.MonitoringPoint;

export type GroupPicturesFilter = Readonly<{
    groupId: string;
    assemblyGroupId?: string | undefined;
    nameSearch: string | undefined;
    groupType?: FilesGroupType[];
}>;

export type GroupPicturesPagedDataSource = PagedDataSource<
    IGroupPictureFileMetadataResponse,
    GroupPicturesFilter
>;

export type GroupPicturesDataSourceOptions = BaseDataSourceOptions<
    IGroupPictureFileMetadataResponse,
    GroupPicturesFilter
>;

const getGroupPictures: DataSourceQuery<
    IGroupPictureFileMetadataResponse,
    GroupPicturesFilter
> = async ({
    filter: { groupId, assemblyGroupId, groupType, nameSearch },
    sort,
    page,
    pageLimit,
}) => {
    const nameSearchFilterExpr = !nameSearch
        ? undefined
        : constructSearchExpression(nameSearch, ['DisplayName', 'Description']);
    const orderBy = buildOrderBy(sort);
    const isAssemblyOnly = groupType?.length === 1 && groupType[0] === GroupType.Assembly;
    const includesAssembly =
        assemblyGroupId &&
        (isAllOrNoFilter(groupType) || groupType?.includes(GroupType.Assembly));

    // Assembly-only filter: query the assembly directly without children
    if (isAssemblyOnly && assemblyGroupId) {
        return toDataSourceResult(
            sdClient.getPicturesForGroup({
                filter: nameSearchFilterExpr,
                orderBy,
                groupId: assemblyGroupId,
                includeChildren: false,
                page,
                pageLimit,
            })
        );
    }

    const groupTypeFilter =
        isAllOrNoFilter(groupType) || !groupType
            ? undefined
            : Filter.in('GroupType', groupType);

    // When assembly files are needed (All view or multi-filter including Assembly),
    // fetch both in parallel. Assembly files are typically few so we fetch all of
    // them unpaginated and merge with the paginated asset/MP results.
    // If the assembly query fails, we degrade gracefully and show asset files only.
    if (includesAssembly) {
        const [assetSettled, assemblySettled] = await Promise.allSettled([
            sdClient.getPicturesForGroup({
                filter: Filter.and(nameSearchFilterExpr, groupTypeFilter),
                orderBy,
                groupId,
                includeChildren: true,
                page,
                pageLimit,
            }),
            sdClient.getPicturesForGroup({
                filter: nameSearchFilterExpr,
                groupId: assemblyGroupId,
                includeChildren: false,
                page: 1,
                pageLimit: 200,
            }),
        ]);

        if (assetSettled.status === 'rejected') {
            throw assetSettled.reason;
        }
        const assetResult = assetSettled.value;

        let assemblyFiles: IGroupPictureFileMetadataResponse[] = [];
        if (assemblySettled.status === 'fulfilled') {
            assemblyFiles = assemblySettled.value.Result;
        } else {
            sentryCaptureMessage({
                message:
                    'Assembly files query failed in file library, degrading gracefully',
                level: 'warning',
                context: {
                    group: { groupId, assemblyGroupId },
                    error:
                        assemblySettled.reason instanceof Error
                            ? { message: assemblySettled.reason.message }
                            : { message: String(assemblySettled.reason) },
                },
            });
        }

        const assetIds = new Set(assetResult.Result.map((r) => r.Id));
        const uniqueAssemblyFiles = assemblyFiles.filter((r) => !assetIds.has(r.Id));

        return toDataSourceResult({
            ...assetResult,
            Result:
                page <= 1
                    ? [...uniqueAssemblyFiles, ...assetResult.Result]
                    : assetResult.Result,
            Total: assetResult.Total + uniqueAssemblyFiles.length,
        });
    }

    // Asset/MP only query
    return toDataSourceResult(
        sdClient.getPicturesForGroup({
            filter: Filter.and(nameSearchFilterExpr, groupTypeFilter),
            orderBy,
            groupId,
            includeChildren: true,
            page,
            pageLimit,
        })
    );
};

export function usePagedGroupFilesDataSource({
    ...options
}: GroupPicturesDataSourceOptions): GroupPicturesPagedDataSource {
    return usePagedDataSource({
        ...options,
        query: getGroupPictures,
    });
}

export const FILE_LIBRARY_TABLE_KEY = 'file-library-table';

export function invalidateFileLibraryTable() {
    queryClient.invalidateQueries({ queryKey: [FILE_LIBRARY_TABLE_KEY] });
}
